import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Navbar } from "@/components/Navbar";
import { ChatAssistant } from "@/components/ChatAssistant";
import { ArrowRight, CheckCircle, Utensils, Pill, Dumbbell, Brain } from "lucide-react";
import { useUI } from "@/context/UIContext";
import { useAuth } from "@/context/AuthContext";

export default function Index() {
  const [scrolled, setScrolled] = useState(false);
  const { theme } = useUI();
  const { user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 10) {
        setScrolled(true);
      } else {
        setScrolled(false);
      }
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const handleGetStarted = () => {
    if (user) {
      navigate("/dashboard");
    } else {
      localStorage.setItem('selectedPlan', 'free');
      localStorage.setItem('selectedPriceId', 'free_tier');
      navigate("/onboarding");
    }
  };

  return (
    <div className={`min-h-screen ${theme}`}>
      <Navbar />
      
      <section className="relative min-h-screen flex items-center justify-center p-4">
        <div 
          className="absolute inset-0 bg-gradient-to-br from-primary/5 to-primary/10"
          style={{
            transform: scrolled ? 'translateY(-10px)' : 'translateY(0)',
            transition: 'transform 0.5s ease-out',
          }}
        />
        
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute -bottom-40 -right-40 w-96 h-96 bg-primary/10 rounded-full filter blur-3xl opacity-70" />
          <div className="absolute -top-40 -left-40 w-96 h-96 bg-primary/10 rounded-full filter blur-3xl opacity-70" />
        </div>
        
        <div className="container max-w-4xl mx-auto text-center relative z-10 mt-24">
          <div className="animate-fade-in-up">
            <h1 className="text-4xl sm:text-5xl md:text-6xl font-display font-bold tracking-tight mb-6">
              Your AI Wellness Partner
            </h1>
            <p className="text-xl md:text-2xl text-muted-foreground mb-8 max-w-2xl mx-auto">
              Personalized nutrition, supplements, fitness, and mindfulness plans designed for your busy lifestyle.
            </p>
            
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-16">
              <Button 
                size="lg" 
                className="text-base"
                onClick={handleGetStarted}
              >
                Get Started
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
              <Button 
                variant="outline" 
                size="lg" 
                className="text-base"
                onClick={() => {
                  window.scrollTo(0, 0);
                  navigate("/pricing");
                }}
              >
                View Plans
              </Button>
            </div>
            
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 max-w-3xl mx-auto">
              {[
                { icon: <Utensils className="h-6 w-6" />, label: "Nutrition Planning" },
                { icon: <Pill className="h-6 w-6" />, label: "Supplement Guidance" },
                { icon: <Dumbbell className="h-6 w-6" />, label: "Fitness Routines" },
                { icon: <Brain className="h-6 w-6" />, label: "Mindfulness Practices" },
              ].map((feature, index) => (
                <div
                  key={index}
                  className="glass-card p-4 flex flex-col items-center text-center animate-fade-in-up"
                  style={{ animationDelay: `${index * 100}ms` }}
                >
                  <div className="h-12 w-12 rounded-full bg-primary/10 flex items-center justify-center mb-3 text-primary">
                    {feature.icon}
                  </div>
                  <p className="text-sm font-medium">{feature.label}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>
      
      <section className="py-24 bg-secondary/50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-display font-bold tracking-tight mb-4">
              Your Complete Wellness Solution
            </h2>
            <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
              Vitatee combines AI technology with evidence-based wellness practices to deliver personalized recommendations.
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-5xl mx-auto">
            {[
              {
                title: "Personalized Plans",
                description: "Receive customized wellness plans tailored to your goals, preferences, and health status."
              },
              {
                title: "AI-Powered Recommendations",
                description: "Our intelligent system adapts to your progress and feedback, continuously improving your plan."
              },
              {
                title: "Holistic Approach",
                description: "Comprehensive wellness support across nutrition, supplementation, fitness, and mindfulness."
              },
              {
                title: "Progress Tracking",
                description: "Visualize your wellness journey with intuitive progress metrics and insights."
              },
              {
                title: "Expert Guidance",
                description: "Access to AI assistant that integrates the latest research in health and wellness."
              },
              {
                title: "Seamless Integration",
                description: "Fits effortlessly into your busy lifestyle with practical, achievable recommendations."
              },
            ].map((feature, index) => (
              <div 
                key={index} 
                className="glass-card p-6 flex flex-col h-full animate-fade-in-up"
                style={{ animationDelay: `${index * 100}ms` }}
              >
                <div className="flex items-center mb-4">
                  <div className="h-8 w-8 rounded-full bg-primary/10 flex items-center justify-center mr-3 text-primary">
                    <CheckCircle className="h-4 w-4" />
                  </div>
                  <h3 className="text-xl font-medium">{feature.title}</h3>
                </div>
                <p className="text-muted-foreground">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
      
      <section className="py-24 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-primary/5 to-primary/10" />
        <div className="absolute -bottom-40 -right-40 w-96 h-96 bg-primary/10 rounded-full filter blur-3xl opacity-70" />
        <div className="absolute -top-40 -left-40 w-96 h-96 bg-primary/10 rounded-full filter blur-3xl opacity-70" />
        
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-3xl mx-auto text-center glass-card p-8 md:p-12 animate-fade-in-up">
            <h2 className="text-3xl md:text-4xl font-display font-bold tracking-tight mb-4">
              Start Your Wellness Journey Today
            </h2>
            <p className="text-xl text-muted-foreground mb-8">
              Join thousands of busy professionals who have transformed their health and well-being with Vitatee.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Button 
                size="lg" 
                className="text-base"
                onClick={handleGetStarted}
              >
                Get Started
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
              <Button 
                variant="outline" 
                size="lg" 
                className="text-base"
                onClick={() => {
                  window.scrollTo(0, 0);
                  navigate("/pricing");
                }}
              >
                View Plans
              </Button>
            </div>
          </div>
        </div>
      </section>
      
      <ChatAssistant />
    </div>
  );
}
